import { useMemo } from 'react'
import { toast } from 'react-toastify'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import BulkCreateAgencyProviderPackagesModal from '@modals/BulkCreateAgencyProviderPackagesModal'
import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CopyAgencyDataModal from '@modals/CopyAgencyDataModal'
import CreateAgencyPublicationsModal from '@modals/CreateAgencyPublicationsModal'
import CreateOrEditAgencyPortalModal from '@modals/CreateOrEditAgencyPortalModal'
import CreateOrEditAgencyProviderDeadlineGroupModal from '@modals/CreateOrEditAgencyProviderDeadlineGroupModal'
import CreateOrEditAgencyProviderModal from '@modals/CreateOrEditAgencyProviderModal'
import CreateOrEditApiKeyModal from '@modals/CreateOrEditApiKeyModal'
import CreateOrEditCustomShortcodeModal from '@modals/CreateOrEditCustomShortcodeModal'
import CreateUserModal from '@modals/CreateUserModal/CreateUserModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageAgencyEnabledStatusModal from '@modals/ManageAgencyEnabledStatusModal'
import ManageAgencyFeaturesModal from '@modals/ManageAgencyFeaturesModal'
import ManageAgencyOptionsModal from '@modals/ManageAgencyOptionsModal'
import ManageAgencyTemplatesModal from '@modals/ManageAgencyTemplatesModal'
import ManageEntitySupportTeamModal from '@modals/ManageEntitySupportTeamModal'

import type {
  ActivityModel,
  AgencyModel,
  AgencyPortalModel,
  AgencyProviderDeadlineGroupModel,
  AgencyProviderModel,
  AgencyProviderPackageModel,
  AgencyPublicationModel,
  ApiKeyModel,
  BrandProviderModel,
  CommentModel,
  CustomShortCodeModel,
  UserModel,
} from '@models/types'

import PageContent from './components/PageContent'

type DefaultState = {
  showBulkCreateAgencyProviderPackagesModal: boolean,
  showBulkCreateBrandProviderModal: boolean,
  showCopyAgencyDataModal: boolean,
  showCreateAgencyPublicationsModal: boolean,
  showCreateOrEditAgencyPortalModal: boolean,
  showCreateOrEditAgencyProviderDeadlineGroupModal: boolean,
  showCreateOrEditAgencyProviderModal: boolean,
  showCreateOrEditApiKeyModal: boolean,
  showCreateOrEditCustomShortcodeModal: boolean,
  showCreateUserModal: boolean,
  showManageActivityModal: boolean,
  showManageAgencyOptionsModal: boolean,
  showManageAgencyEnabledStatusModal: boolean,
  showManageAgencyFeaturesModal: boolean,
  showManageAgencyTemplatesModal: boolean,
  showManageEntitySupportTeamModal: boolean,
}

const defaultState = {
  showBulkCreateAgencyProviderPackagesModal: false,
  showBulkCreateBrandProviderModal: false,
  showCopyAgencyDataModal: false,
  showCreateAgencyPublicationsModal: false,
  showCreateOrEditAgencyPortalModal: false,
  showCreateOrEditAgencyProviderDeadlineGroupModal: false,
  showCreateOrEditAgencyProviderModal: false,
  showCreateOrEditApiKeyModal: false,
  showCreateOrEditCustomShortcodeModal: false,
  showCreateUserModal: false,
  showManageActivityModal: false,
  showManageAgencyEnabledStatusModal: false,
  showManageAgencyOptionsModal: false,
  showManageAgencyTemplatesModal: false,
  showManageAgencyFeaturesModal: false,
  showManageEntitySupportTeamModal: false,
}

const createComment = (
  params: Partial<CommentModel>,
  createFn: (createParams: Partial<CommentModel>, options: object) => Promise<any>,
) => {
  createFn(params, { ...defaultRequestOptions.comment, ...defaultRequestOptions.conversation })
    .then(({ success, errors }) => {
      if (!success && errors){
        toast.warn(errors[0])
      }
    })
}

const updateAgencyEnabled = (
  commentParams: Partial<CommentModel>,
  agencyParams: Partial<AgencyModel>,
  createCommentFn: (params: Partial<CommentModel>, options: object) => Promise<any>,
  updateAgencyFn: (params: Partial<AgencyModel>) => Promise<any>,
  setState: (state: Partial<DefaultState>) => void,
) => {
  updateAgencyFn(agencyParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    toast(`Agency ${agencyParams.active ? 'Enabled' : 'Disabled'} Successfully`)
    setState({ showManageAgencyEnabledStatusModal: false })

    createComment(commentParams, createCommentFn)
  })
}

// @ts-expect-error TS7006
const updateAgencyTemplatePermissions = (permissionAction, serviceId, updateFn) => (
  // @ts-expect-error TS7031
  updateFn(permissionAction, serviceId).then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    toast('Templates Updated Successfully')
  })
)

type SetState = (state: Partial<DefaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateAgencyProviderPackagesModal: {
      closeModal: () => setState({ showBulkCreateAgencyProviderPackagesModal: false }),
      bulkCreateAgencyProviderPackages: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    BulkCreateBrandProviderModal: {
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
      bulkCreateBrandProviders: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CopyAgencyDataModal: {
      closeModal: () => setState({ showCopyAgencyDataModal: false }),
      copyAgencyData: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyPortalModal: {
      closeModal: () => setState({ showCreateOrEditAgencyPortalModal: false }),
      createAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      generateAgencyPortalAuthToken: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyProviderDeadlineGroupModal: {
      closeModal: () => setState({ showCreateOrEditAgencyProviderDeadlineGroupModal: false }),
      createAgencyProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAgencyProviderModal: {
      closeModal: () => setState({ showCreateOrEditAgencyProviderModal: false }),
      createAgencyProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditApiKeyModal: {
      closeModal: () => setState({ showCreateOrEditApiKeyModal: false }),
      createApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCustomShortcodeModal: {
      closeModal: () => setState({ showCreateOrEditCustomShortcodeModal: false }),
      createCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateUserModal: {
      closeModal: () => setState({ showCreateUserModal: false }),
      createUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    CreateAgencyPublicationsModal: {
      bulkCreateAgencyPublications: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showCreateAgencyPublicationsModal: false }),
    },
    ManageAgencyEnabledStatusModal: {
      closeModal: () => setState({ showManageAgencyEnabledStatusModal: false }),
      updateAgencyEnabled: (
        commentParams: Partial<CommentModel>,
        agencyParams: Partial<AgencyModel>,
        createCommentFn: (params: Partial<CommentModel>, options: object) => Promise<any>,
        updateAgencyFn: (params: Partial<AgencyModel>) => Promise<any>,
      ) => (
        updateAgencyEnabled(commentParams, agencyParams, createCommentFn, updateAgencyFn, setState)
      ),
    },
    ManageAgencyFeaturesModal: {
      closeModal: () => setState({ showManageAgencyFeaturesModal: false }),
      // @ts-expect-error TS7006
      updateAgency: payload => handleCallbackAction(payload),
    },
    ManageAgencyOptionsModal: {
      closeModal: () => setState({ showManageAgencyOptionsModal: false }),
      // @ts-expect-error TS7006
      updateAgency: payload => handleCallbackAction(payload),
    },
    ManageAgencyTemplatesModal: {
      closeModal: () => setState({ showManageAgencyTemplatesModal: false }),
      updateAgencyTemplatePermissions,
    },
    ManageEntitySupportTeamModal: {
      closeModal: () => setState({ showManageEntitySupportTeamModal: false }),
      updateEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },

  }

  return componentCallbacks[component] || {}
}

const Relations = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateAgencyProviderPackagesModal,
    showBulkCreateBrandProviderModal,
    showCopyAgencyDataModal,
    showCreateAgencyPublicationsModal,
    showCreateOrEditAgencyPortalModal,
    showCreateOrEditAgencyProviderDeadlineGroupModal,
    showCreateOrEditAgencyProviderModal,
    showCreateOrEditApiKeyModal,
    showCreateOrEditCustomShortcodeModal,
    showCreateUserModal,
    showManageActivityModal,
    showManageAgencyEnabledStatusModal,
    showManageAgencyFeaturesModal,
    showManageAgencyOptionsModal,
    showManageAgencyTemplatesModal,
    showManageEntitySupportTeamModal,
  } = state

  const { agencyId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      createComment,
      deleteAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyProviderDeadlineGroup: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyProviderPackage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyPublication: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      exportAgency: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      generateAgencyPortalAuthToken: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showBulkCreateAgencyProviderPackagesModal: (payload: AgencyProviderPackageModel) => {
        setModalData('BulkCreateAgencyProviderPackagesModal', payload)
        setState({ showBulkCreateAgencyProviderPackagesModal: true })
      },
      showBulkCreateBrandProviderModal: (payload: BrandProviderModel) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      showCopyAgencyDataModal: (payload: AgencyModel) => {
        setModalData('CopyAgencyDataModal', payload)
        setState({ showCopyAgencyDataModal: true })
      },
      showCreateOrEditAgencyProviderDeadlineGroupModal: (payload: AgencyProviderDeadlineGroupModel) => {
        setModalData('CreateOrEditAgencyProviderDeadlineGroupModal', payload)
        setState({ showCreateOrEditAgencyProviderDeadlineGroupModal: true })
      },
      showCreateOrEditAgencyProviderModal: (payload: AgencyProviderModel) => {
        setModalData('CreateOrEditAgencyProviderModal', payload)
        setState({ showCreateOrEditAgencyProviderModal: true })
      },
      showCreateAgencyPublicationsModal: (payload: AgencyPublicationModel) => {
        setModalData('CreateAgencyPublicationsModal', payload)
        setState({ showCreateAgencyPublicationsModal: true })
      },
      showCreateOrEditAgencyPortalModal: (payload: AgencyPortalModel) => {
        setModalData('CreateOrEditAgencyPortalModal', payload)
        setState({ showCreateOrEditAgencyPortalModal: true })
      },
      showCreateOrEditApiKeyModal: (payload: ApiKeyModel) => {
        setModalData('CreateOrEditApiKeyModal', payload)
        setState({ showCreateOrEditApiKeyModal: true })
      },
      showCreateOrEditCustomShortcodeModal: (payload: CustomShortCodeModel) => {
        setModalData('CreateOrEditCustomShortcodeModal', payload)
        setState({ showCreateOrEditCustomShortcodeModal: true })
      },
      showCreateUserModal: (payload: UserModel) => {
        setModalData('CreateUserModal', payload)
        setState({ showCreateUserModal: true })
      },
      showManageActivityModal: (payload: ActivityModel) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageAgencyEnabledStatusModal: (payload: AgencyModel) => {
        setModalData('ManageAgencyEnabledStatusModal', payload)
        setState({ showManageAgencyEnabledStatusModal: true })
      },
      // @ts-expect-error TS7006
      showManageAgencyFeaturesModal: (payload) => {
        setModalData('ManageAgencyFeaturesModal', payload)
        setState({ showManageAgencyFeaturesModal: true })
      },
      // @ts-expect-error TS7006
      showManageAgencyOptionsModal: (payload) => {
        setModalData('ManageAgencyOptionsModal', payload)
        setState({ showManageAgencyOptionsModal: true })
      },
      // @ts-expect-error TS7006
      showManageAgencyTemplatesModal: (payload) => {
        setModalData('ManageAgencyTemplatesModal', payload)
        setState({ showManageAgencyTemplatesModal: true })
      },
      showManageEntitySupportTeamModal: (payload: object) => {
        setModalData('ManageEntitySupportTeamModal', payload)
        setState({ showManageEntitySupportTeamModal: true })
      },
      toggleAgencyLock: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent agencyId={agencyId} />

        <BulkCreateAgencyProviderPackagesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkCreateAgencyProviderPackagesModal', setState)}
          showModal={showBulkCreateAgencyProviderPackagesModal}
        />

        <BulkCreateBrandProviderModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CopyAgencyDataModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CopyAgencyDataModal', setState)}
          showModal={showCopyAgencyDataModal}
        />

        <CreateOrEditAgencyProviderDeadlineGroupModal
          callbacks={callbacks('CreateOrEditAgencyProviderDeadlineGroupModal', setState)}
          showModal={showCreateOrEditAgencyProviderDeadlineGroupModal}
        />

        <CreateAgencyPublicationsModal
          callbacks={callbacks('CreateAgencyPublicationsModal', setState)}
          showModal={showCreateAgencyPublicationsModal}
        />

        <CreateOrEditAgencyPortalModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAgencyPortalModal', setState)}
          showModal={showCreateOrEditAgencyPortalModal}
        />

        <CreateOrEditAgencyProviderModal
          callbacks={callbacks('CreateOrEditAgencyProviderModal', setState)}
          showModal={showCreateOrEditAgencyProviderModal}
        />

        <CreateOrEditApiKeyModal
          callbacks={callbacks('CreateOrEditApiKeyModal', setState)}
          showModal={showCreateOrEditApiKeyModal}
        />

        <CreateOrEditCustomShortcodeModal
          callbacks={callbacks('CreateOrEditCustomShortcodeModal', setState)}
          showModal={showCreateOrEditCustomShortcodeModal}
        />

        <CreateUserModal
          callbacks={callbacks('CreateUserModal', setState)}
          showModal={showCreateUserModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageAgencyOptionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageAgencyOptionsModal', setState)}
          showModal={showManageAgencyOptionsModal}
        />

        <ManageAgencyFeaturesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageAgencyFeaturesModal', setState)}
          showModal={showManageAgencyFeaturesModal}
        />

        <ManageAgencyEnabledStatusModal
          callbacks={callbacks('ManageAgencyEnabledStatusModal', setState)}
          showModal={showManageAgencyEnabledStatusModal}
        />

        <ManageAgencyTemplatesModal
          callbacks={callbacks('ManageAgencyTemplatesModal', setState)}
          showModal={showManageAgencyTemplatesModal}
        />

        <ManageEntitySupportTeamModal
          callbacks={callbacks('ManageEntitySupportTeamModal', setState)}
          showModal={showManageEntitySupportTeamModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
